/**
 * BdcSettingsPayment — ACH payout setup for BDC agents (Phase 11 rewrite).
 *
 * The PDF upload flow was retired. BDC agents now enter their routing +
 * account number directly. The account number is encrypted at rest by the
 * backend (Fernet via ach_crypto); only the last-4 comes back to the UI.
 *
 *   POST /api/bdc/settings/ach       — upsert
 *   GET  /api/bdc/settings/payout    — read-back (masked)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Banknote, CheckCircle2, Clock, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageContainer, PageHeader } from "./bdcUi";

const ACCT_TYPES = [
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
];

const EMPTY_FORM = {
  account_holder_name: "",
  bank_name: "",
  routing_number: "",
  account_number: "",
  account_type: "checking",
};

const BdcSettingsPayment = () => {
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bdc/settings/payout");
      setPayout(data);
      // Pre-fill the form with what's already on file (except the account #)
      setForm({
        account_holder_name: data?.account_holder_name || "",
        bank_name: data?.bank_name || "",
        routing_number: data?.routing_number || "",
        account_number: "",
        account_type: data?.account_type || "checking",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = () => {
    const errs = {};
    if (!form.account_holder_name.trim()) errs.account_holder_name = "Required";
    if (!/^\d{9}$/.test(form.routing_number || "")) errs.routing_number = "Must be 9 digits";
    if (!/^\d{4,17}$/.test(form.account_number || "")) errs.account_number = "Enter 4–17 digits";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/bdc/settings/ach", {
        account_holder_name: form.account_holder_name.trim(),
        bank_name: form.bank_name.trim() || undefined,
        routing_number: form.routing_number,
        account_number: form.account_number,
        account_type: form.account_type,
      });
      toast.success("Banking info saved · awaiting admin verification");
      // Merge saved fields into the read-back display
      setPayout({
        ...(payout || {}),
        on_file: true,
        verified: false,
        account_holder_name: data.account_holder_name,
        bank_name: data.bank_name,
        routing_number: data.routing_number,
        account_masked: data.account_masked,
        account_number_last4: data.account_masked?.slice(-4),
        account_type: data.account_type,
      });
      setForm((f) => ({ ...f, account_number: "" }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onDigits = (name) => (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setForm((f) => ({ ...f, [name]: digits }));
  };

  const hasForm = payout?.on_file;
  const verified = payout?.verified;

  return (
    <PageContainer>
      <PageHeader
        title="Payment setup"
        subtitle="Enter your bank details to receive commission payouts."
        testId="bdc-payment-header"
      />

      {/* Payouts info banner */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3 mb-6" data-testid="bdc-payouts-info">
        <Info size={18} className="text-blue-600 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-900">
          <p><strong>Payments processed every Friday.</strong></p>
          <p className="text-[13px] text-blue-800 mt-0.5">
            For questions, contact <a href="mailto:accounting@belomotors.com" className="underline font-semibold">accounting@belomotors.com</a>.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <form
          onSubmit={submit}
          className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 space-y-5"
          data-testid="bdc-ach-form"
        >
          <div className="flex items-center gap-2">
            <Banknote size={16} className="text-brand" />
            <h2 className="font-display text-lg font-semibold text-slate-900">Bank details</h2>
          </div>

          {loading ? (
            <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />
          ) : (
            <>
              {hasForm && payout?.account_masked && (
                <div
                  className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center justify-between"
                  data-testid="bdc-ach-current"
                >
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">Currently on file</p>
                    <p className="text-slate-600 text-xs mt-0.5">
                      Account: <span className="font-mono">{payout.account_masked}</span>
                      {" · "}Routing: <span className="font-mono">{payout.routing_number}</span>
                      {" · "}<span className="capitalize">{payout.account_type}</span>
                    </p>
                  </div>
                  {verified ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-1" data-testid="bdc-ach-verified">
                      <CheckCircle2 size={12} /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold px-2.5 py-1" data-testid="bdc-ach-pending">
                      <Clock size={12} /> Pending verification
                    </span>
                  )}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="account_holder_name">Account holder name *</Label>
                  <Input
                    id="account_holder_name"
                    value={form.account_holder_name}
                    onChange={(e) => setForm((f) => ({ ...f, account_holder_name: e.target.value }))}
                    placeholder="Sam Rivera"
                    data-testid="bdc-ach-holder-input"
                  />
                  {errors.account_holder_name && <p className="text-xs text-rose-600 mt-1">{errors.account_holder_name}</p>}
                </div>
                <div>
                  <Label htmlFor="bank_name">Bank name (optional)</Label>
                  <Input
                    id="bank_name"
                    value={form.bank_name}
                    onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                    placeholder="Wells Fargo"
                    data-testid="bdc-ach-bank-input"
                  />
                </div>
                <div>
                  <Label htmlFor="routing_number">Routing number * <span className="text-xs text-slate-400">(9 digits)</span></Label>
                  <Input
                    id="routing_number"
                    value={form.routing_number}
                    onChange={onDigits("routing_number")}
                    maxLength={9}
                    inputMode="numeric"
                    placeholder="011401533"
                    className="font-mono"
                    data-testid="bdc-ach-routing-input"
                  />
                  {errors.routing_number && <p className="text-xs text-rose-600 mt-1">{errors.routing_number}</p>}
                </div>
                <div>
                  <Label htmlFor="account_number">
                    Account number *
                    {hasForm && <span className="text-xs text-slate-400 ml-2">(leave blank to keep current)</span>}
                  </Label>
                  <Input
                    id="account_number"
                    type="password"
                    value={form.account_number}
                    onChange={onDigits("account_number")}
                    maxLength={17}
                    inputMode="numeric"
                    placeholder={hasForm ? "●●●●●●●●●●" : "1234567890"}
                    className="font-mono"
                    data-testid="bdc-ach-account-input"
                  />
                  {errors.account_number && <p className="text-xs text-rose-600 mt-1">{errors.account_number}</p>}
                </div>
                <div className="sm:col-span-2">
                  <Label>Account type *</Label>
                  <div className="mt-1 inline-flex rounded-full border border-slate-200 p-1">
                    {ACCT_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, account_type: t.value }))}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium ${
                          form.account_type === t.value ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                        }`}
                        data-testid={`bdc-ach-type-${t.value}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button type="submit" disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="bdc-ach-submit-btn">
                  {saving && <Loader2 size={14} className="animate-spin mr-2" />}
                  {hasForm ? "Update banking info" : "Save banking info"}
                </Button>
              </div>
            </>
          )}
        </form>

        {/* Info sidebar */}
        <aside className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3 text-sm text-slate-600">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Commission rate</p>
            <p className="mt-1"><span className="font-semibold text-slate-900">$50</span> per confirmed showed-up appointment credited to you.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Payout schedule</p>
            <p className="mt-1">Every <span className="font-semibold text-slate-900">Friday</span>, processed by Belomotors accounting.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Verification</p>
            <p className="mt-1 text-xs">Admin verifies your details within 2 business days. You'll see a green check when approved.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Security</p>
            <p className="mt-1 text-xs">Your account number is encrypted at rest. Only the last 4 digits are ever displayed.</p>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
};

export default BdcSettingsPayment;
